import { useState, useCallback } from 'react';
import { HexColorPicker } from 'react-colorful';
import { Sun, Moon, Monitor, Check } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useThemeStore } from '@/stores/theme-store';
import {
  THEME_PRESETS,
  PRESET_DISPLAY_NAMES,
  generateShadeScale,
  type ThemePresetId,
  type ThemeMode
} from '@/lib/theme-engine';

// ─── Mode section ─────────────────────────────────────────────────────────────

type ModeOption = {
  value: ThemeMode;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  description: string;
};

const modeOptions: ModeOption[] = [
  { value: 'light', label: 'Light', icon: Sun, description: 'Always use light theme' },
  { value: 'dark', label: 'Dark', icon: Moon, description: 'Always use dark theme' },
  { value: 'system', label: 'System', icon: Monitor, description: 'Follows your OS preference' }
];

// ─── Preset swatches ──────────────────────────────────────────────────────────

const PRESET_IDS = Object.keys(THEME_PRESETS) as ThemePresetId[];

// ─── Custom color section ─────────────────────────────────────────────────────

const HEX_REGEX = /^#[0-9a-fA-F]{6}$/;

function isValidHex(value: string): boolean {
  return HEX_REGEX.test(value);
}

function CustomColorSection() {
  const { customColor, setCustomColor } = useThemeStore();
  const [pickerColor, setPickerColor] = useState<string>(customColor ?? '#2563EB');
  const [input, setInput] = useState<string>(customColor ?? '');

  // Debounced apply on picker drag
  const handlePickerChange = useCallback(
    (color: string) => {
      setPickerColor(color);
      setInput(color);
      setCustomColor(color);
    },
    [setCustomColor]
  );

  function handleInputChange(e: React.ChangeEvent<HTMLInputElement>): void {
    const value = e.target.value;
    setInput(value);
    if (isValidHex(value)) {
      setPickerColor(value);
      setCustomColor(value);
    }
  }

  function handleClear(): void {
    setInput('');
    setPickerColor('#2563EB');
    setCustomColor(null);
  }

  const activeColor = customColor ?? pickerColor;
  const shades = isValidHex(activeColor) ? generateShadeScale(activeColor) : null;

  return (
    <div className="space-y-4">
      {/* Color picker + hex input side by side */}
      <div className="flex gap-4">
        <div className="shrink-0 [&_.react-colorful]:w-44 [&_.react-colorful]:h-44 [&_.react-colorful]:rounded-lg">
          <HexColorPicker color={pickerColor} onChange={handlePickerChange} />
        </div>
        <div className="flex flex-1 flex-col gap-3">
          <div>
            <label className="mb-1 block text-xs font-medium text-muted-foreground">Hex</label>
            <input
              type="text"
              value={input}
              onChange={handleInputChange}
              placeholder="#2563EB"
              maxLength={7}
              className={cn(
                'w-full rounded-md border px-3 py-2 text-sm font-mono',
                'bg-background text-foreground placeholder:text-muted-foreground',
                'focus:outline-none focus:ring-2 focus:ring-ring',
                !isValidHex(input) && input.length > 0 ? 'border-destructive' : 'border-input'
              )}
            />
          </div>
          <div
            className="h-12 rounded-md border border-border"
            style={{ backgroundColor: activeColor }}
            aria-label={`Preview color ${activeColor}`}
          />
          {customColor && (
            <button
              onClick={handleClear}
              className="self-start rounded-md px-3 py-1.5 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors border border-border hover:border-foreground/20"
            >
              Reset to preset
            </button>
          )}
        </div>
      </div>

      {/* Shade scale preview */}
      {shades && (
        <div className="flex gap-0.5 rounded-md overflow-hidden">
          {['50', '100', '200', '300', '400', '500', '600', '700', '800', '900', '950'].map(
            (key) => (
              <div
                key={key}
                className="h-6 flex-1"
                style={{ backgroundColor: shades[key] }}
                title={`${key}: ${shades[key]}`}
              />
            )
          )}
        </div>
      )}
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export function AppearanceTab() {
  const { mode, preset, fontSize, compact, setMode, setPreset, setFontSize, setCompact } =
    useThemeStore();

  return (
    <div className="space-y-8">
      {/* Section 1: Theme Mode */}
      <div>
        <h3 className="mb-1 text-sm font-semibold">Theme</h3>
        <p className="mb-4 text-sm text-muted-foreground">
          Choose how Eligaro looks on your device.
        </p>
        <div className="grid grid-cols-3 gap-3">
          {modeOptions.map((option) => {
            const Icon = option.icon;
            const isActive = mode === option.value;
            return (
              <button
                key={option.value}
                onClick={() => setMode(option.value)}
                className={cn(
                  'flex flex-col items-center gap-3 rounded-lg border p-4 text-center transition-colors',
                  isActive
                    ? 'border-primary bg-primary/5 text-primary'
                    : 'border-border text-muted-foreground hover:border-foreground hover:text-foreground'
                )}
              >
                <Icon className="h-6 w-6" />
                <div>
                  <p className="text-sm font-medium">{option.label}</p>
                  <p className="mt-0.5 text-xs text-muted-foreground">{option.description}</p>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Section 2: Color Scheme */}
      <div>
        <h3 className="mb-1 text-sm font-semibold">Color Scheme</h3>
        <p className="mb-4 text-sm text-muted-foreground">
          Pick a color palette for the entire interface.
        </p>
        <div className="grid grid-cols-4 gap-4 sm:grid-cols-7">
          {PRESET_IDS.map((id) => {
            const p = THEME_PRESETS[id];
            const isActive = preset === id;
            return (
              <button
                key={id}
                onClick={() => setPreset(id)}
                className="flex flex-col items-center gap-2"
                aria-label={`Select ${PRESET_DISPLAY_NAMES[id]} theme`}
                aria-pressed={isActive}
              >
                <div
                  className={cn(
                    'relative flex h-10 w-10 items-center justify-center rounded-full transition-all',
                    isActive ? 'ring-2 ring-offset-2 ring-primary' : 'hover:scale-110'
                  )}
                  style={{ backgroundColor: p.shades['500'] }}
                >
                  {isActive && <Check className="h-5 w-5 text-white drop-shadow" />}
                </div>
                <span className="text-center text-xs text-muted-foreground leading-tight">
                  {PRESET_DISPLAY_NAMES[id]}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Section 3: Custom Color */}
      <div>
        <h3 className="mb-1 text-sm font-semibold">Custom Color</h3>
        <p className="mb-4 text-sm text-muted-foreground">
          Pick any color to generate a custom palette.
        </p>
        <CustomColorSection />
      </div>

      {/* Section 4: Font Size */}
      <div>
        <h3 className="mb-1 text-sm font-semibold">Font Size</h3>
        <p className="mb-4 text-sm text-muted-foreground">Adjust text size across the interface.</p>
        <div className="flex rounded-md border border-border">
          {(['small', 'default', 'large'] as const).map((size) => (
            <button
              key={size}
              onClick={() => setFontSize(size)}
              className={cn(
                'flex-1 px-4 py-2 text-sm font-medium transition-colors first:rounded-l-md last:rounded-r-md',
                fontSize === size
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:text-foreground hover:bg-secondary'
              )}
            >
              {size === 'small'
                ? 'Small (14px)'
                : size === 'large'
                  ? 'Large (18px)'
                  : 'Default (16px)'}
            </button>
          ))}
        </div>
      </div>

      {/* Section 5: Compact Mode */}
      <div>
        <h3 className="mb-1 text-sm font-semibold">Compact Mode</h3>
        <p className="mb-4 text-sm text-muted-foreground">
          Reduce spacing for a denser layout. Does not affect text size.
        </p>
        <button
          role="switch"
          aria-checked={compact}
          onClick={() => setCompact(!compact)}
          className={cn(
            'relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
            compact ? 'bg-primary' : 'bg-muted'
          )}
        >
          <span
            className={cn(
              'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out',
              compact ? 'translate-x-5' : 'translate-x-0'
            )}
          />
        </button>
      </div>
    </div>
  );
}
